/**
 * Pre-made response queue and conversation history.
 *
 * When a pre-made suggestion is clicked the response is queued here.
 * The chat model adapter checks for pending responses before calling
 * the real API and returns the queued content instead.
 */

export interface PreMadeResponse {
  question: string;
  response: string;
  streamingSpeed?: number;
  initialDelay?: number;
}

export interface PreMadeMessage {
  role: "user" | "assistant";
  content: string;
}

const queue: PreMadeResponse[] = [];
let conversationHistory: PreMadeMessage[] = [];

/**
 * Queue a pre-made response to be returned instead of calling the API.
 * Call this before triggering the normal send flow.
 */
export function queuePreMadeResponse(
  question: string,
  response: string,
  streamingSpeed: number = 50,
  initialDelay: number = 800,
): void {
  queue.push({ question, response, streamingSpeed, initialDelay });
}

export function hasPendingPreMadeResponse(): boolean {
  return queue.length > 0;
}

export function shiftPendingPreMade(): PreMadeResponse | undefined {
  return queue.shift();
}

export function clearPreMadeResponses(): void {
  queue.length = 0;
  conversationHistory = [];
}

export function getPreMadeConversationHistory(): PreMadeMessage[] {
  return conversationHistory;
}

export function appendToConversationHistory(
  question: string,
  answer: string,
): void {
  conversationHistory.push(
    { role: "user", content: question },
    { role: "assistant", content: answer },
  );
}

export function clearConversationHistory(): void {
  conversationHistory = [];
}
